import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { TransactionDto } from '@trestly/shared';
import { ApiError } from './api/client';
import { useDeleteTransaction } from './api/hooks';
import { EntrySheet, readLastCategory } from './components/EntrySheet';
import { useToast } from './components/Toast';
import { useEnvelopeLook } from './lib/envelope';
import { moneyLabel } from './lib/money';
import { haptic } from './telegram';
import { t } from './i18n';

interface QuickExpenseContextValue {
  /**
   * Открыть ввод траты. categoryId — конверт, из которого открыли (null —
   * «Без категории»); не передан — последний использованный конверт.
   */
  open: (options?: { categoryId?: number | null }) => void;
}

const QuickExpenseContext = createContext<QuickExpenseContextValue | null>(null);

/**
 * Лист траты — один на всё приложение: его открывают плитки конвертов, экран
 * конверта и «+» в ленте. После записи — тост с «Отменить» вместо
 * подтверждения до записи.
 */
export function QuickExpenseProvider({ children }: { children: ReactNode }): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  // Открыли «+» без конверта — сразу показываем выбор: иначе трата молча уйдёт в прошлый конверт.
  const [pick, setPick] = useState(false);
  const toast = useToast();
  const lookFor = useEnvelopeLook();
  const deleteTransaction = useDeleteTransaction();

  const open = useCallback((options?: { categoryId?: number | null }) => {
    haptic.tap('medium');
    const explicit = options !== undefined && 'categoryId' in options;
    setCategoryId(explicit ? (options.categoryId ?? null) : readLastCategory());
    setPick(!explicit);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const onCreated = useCallback(
    (tx: TransactionDto) => {
      toast.show({
        text: (
          <>
            <span className="num font-semibold">{moneyLabel(tx.amountMinor, tx.currency)}</span>
            <span className="opacity-70"> · {lookFor(tx.categoryId).name}</span>
          </>
        ),
        action: {
          label: t('app.undo'),
          onClick: () =>
            deleteTransaction.mutate(tx.id, {
              onSuccess: () => haptic.result('warning'),
              // 404 — трату уже удалили вручную за эти секунды: цель достигнута.
              onError: (error) => {
                if (!(error instanceof ApiError && error.statusCode === 404)) haptic.result('error');
              },
            }),
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lookFor, toast],
  );

  const value = useMemo(() => ({ open }), [open]);

  return (
    <QuickExpenseContext.Provider value={value}>
      {children}
      <EntrySheet open={isOpen} categoryId={categoryId} pick={pick} onClose={close} onCreated={onCreated} />
    </QuickExpenseContext.Provider>
  );
}

export function useQuickExpense(): QuickExpenseContextValue {
  const ctx = useContext(QuickExpenseContext);
  if (!ctx) throw new Error('useQuickExpense must be used inside QuickExpenseProvider');
  return ctx;
}
